import { CellSelection } from '@_ueberdosis/prosemirror-tables';
import { Editor, Node, mergeAttributes } from '@tiptap/core';
import { nanoid } from 'nanoid';
import { Node as ProseMirrorModelNode } from 'prosemirror-model';
import { TextSelection, Transaction } from 'prosemirror-state';
import { isLight } from 'utils/color';

const checkboxIcon = `<svg class="prosemirror-checkbox-icon" width="100%" height="100%" viewBox="0 0 12 12" fill="none" color="inherit" xmlns="http://www.w3.org/2000/svg"><path d="M2.5 6.09998L5 8.59998L10 3.59998" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round"/></svg>`;

export interface CheckboxOptions {
  HTMLAttributes: Record<string, any>;

  // Whether to show an nimation when the checkbox is checked
  hasCheckedAnimation?: boolean;

  // Triggered when the checkbox is clicked
  onChange?: (checkboxId: string, isChecked: boolean) => void;
  checkboxType: 'circle' | 'square';
}

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    checkbox: {
      /**
       * Add a checkbox to the editor
       */
      insertCheckbox: (type: CheckboxOptions['checkboxType']) => ReturnType;
      /**
       * Update the border color and background color of the checkbox
       */
      onChangeColor: (color: string, type: string) => ReturnType;
    };
  }
}

export const Checkbox = Node.create<CheckboxOptions>({
  name: 'checkbox',

  group: 'inline',

  inline: true,

  selectable: true,

  atom: true,

  addAttributes() {
    return {
      id: {
        default: null,
      },
      isChecked: {
        default: false,
      },
      type: {
        default: 'square',
      },
      borderColor: {
        default: 'var(--vg-colors-font-500)',
      },
      backgroundColor: {
        default: 'transparent',
      },
    };
  },

  parseHTML() {
    return [
      {
        tag: 'input[type="checkbox"]',
        getAttrs: (dom: string | HTMLElement) => {
          const el = dom as HTMLElement;
          return {
            isChecked: el.getAttribute('checked') === 'true',
            id: el.getAttribute('data-id'),
          };
        },
      },
    ];
  },

  renderHTML({ HTMLAttributes }) {
    const isChecked = HTMLAttributes.isChecked;
    const id = HTMLAttributes.id;
    delete HTMLAttributes.isChecked;
    delete HTMLAttributes.id;
    return [
      'input',
      mergeAttributes(this.options.HTMLAttributes, HTMLAttributes, {
        type: 'checkbox',
        checked: isChecked,
        'data-id': id,
      }),
    ];
  },

  // TODO - if this gets too complex, can consider creating a ReactNodeView
  addNodeView() {
    return ({ node, getPos, editor }) => {
      const wrapper = document.createElement('label');
      wrapper.setAttribute('contenteditable', 'false');
      wrapper.setAttribute('tabindex', '0');
      wrapper.style.cursor = 'pointer';
      wrapper.style.display = 'inline-flex';
      wrapper.style.alignItems = 'center'; // Center vertically
      wrapper.style.verticalAlign = 'middle';
      wrapper.className = 'prosemirror-checkbox-wrapper';
      const checkboxStyle = node.attrs.type;

      if (this.options.HTMLAttributes?.style) {
        const existingStyle = wrapper.getAttribute('style');
        wrapper.setAttribute('style', `${existingStyle}; ${this.options.HTMLAttributes.style}`);
      }

      const input = document.createElement('input');
      input.type = 'checkbox';
      input.checked = node.attrs.isChecked;
      input.setAttribute('data-id', node.attrs.id);
      input.style.width = '0px';
      input.style.height = '0px';
      input.style.visibility = 'hidden';

      const span = document.createElement('span');
      span.className = 'prosemirror-checkbox';
      if (this.options.hasCheckedAnimation) {
        span.classList?.add('animated');
      } else if (span.classList?.contains('animated')) {
        span.classList?.remove('animated');
      }

      const borderColor = node.attrs.borderColor;
      const backgroundColor = node.attrs.backgroundColor;
      const isBackgroundLight = isLight(backgroundColor === 'transparent' ? '#ffffff' : backgroundColor);

      span.style.display = 'inline-flex';
      span.style.justifyContent = 'center';
      span.style.alignItems = 'center';
      span.style.width = checkboxStyle === 'circle' ? '22px' : '20px';
      span.style.height = checkboxStyle === 'circle' ? '22px' : '20px';
      span.style.borderRadius = checkboxStyle === 'circle' ? '50%' : '4px';
      span.style.padding = '2px';
      span.style.border = `1px solid ${borderColor}`;
      span.style.backgroundColor = backgroundColor;
      span.style.color = isBackgroundLight ? '#000000' : '#ffffff';
      span.innerHTML = node.attrs.isChecked ? checkboxIcon : '';

      const handleClick = async () => {
        if (!editor) return;
        if (typeof getPos !== 'function') return;

        // Note: don't need to toggle input, it's handled by default click event handler for <label> element
        const isChecked = !input.checked;

        // Handle onChange event if provided
        let isCheckSuccessful = true;
        if (typeof this.options.onChange === 'function') {
          // Disable the checkbox while the onChange is being called
          input.disabled = true;
          span.style.background = 'var(--vg-colors-gray-200)';
          wrapper.style.cursor = 'not-allowed';
          const oldPointerEvent = wrapper.style.pointerEvents;
          wrapper.style.pointerEvents = 'none';

          // Trigger on change
          try {
            await this.options.onChange(node.attrs.id, isChecked);
          } catch (err) {
            isCheckSuccessful = false;
          }

          // Re-enable the checkbox - set a delay to prevent rapid clicking
          await new Promise((resolve) => setTimeout(resolve, 100));
          wrapper.style.pointerEvents = oldPointerEvent;
          wrapper.style.cursor = 'pointer';
          span.style.background = 'transparent';
          input.disabled = false;
        }

        // If there was an issue with the onChange handler, don't update the checkbox
        if (!isCheckSuccessful) return;

        // Toggle the checkbox on click
        const isBackgroundLight = isLight(backgroundColor === 'transparent' ? '#ffffff' : backgroundColor);
        span.style.color = isBackgroundLight ? '#000000' : '#ffffff';
        span.innerHTML = isChecked ? checkboxIcon : '';
        const transaction = editor.view.state.tr.setNodeMarkup(getPos(), undefined, {
          ...node.attrs,
          isChecked,
        });
        editor.view.dispatch(transaction);
      };

      wrapper.addEventListener('click', handleClick);
      if (this.options.hasCheckedAnimation) {
        wrapper.addEventListener('keydown', (e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleClick();
          }
        });
      }

      wrapper.append(input);
      wrapper.append(span);

      return {
        dom: wrapper,
        update: (updatedNode: any) => {
          if (updatedNode.type !== node.type) {
            return false;
          }
          const checkboxStyle = updatedNode.attrs.type;
          const borderColor = updatedNode.attrs.borderColor;
          const backgroundColor = updatedNode.attrs.backgroundColor;
          const isBackgroundLight = isLight(backgroundColor === 'transparent' ? '#ffffff' : backgroundColor);

          input.checked = updatedNode.attrs.isChecked;
          span.style.color = isBackgroundLight ? '#000000' : '#ffffff';
          span.innerHTML = updatedNode.attrs.isChecked ? checkboxIcon : '';
          span.style.width = checkboxStyle === 'circle' ? '22px' : '20px';
          span.style.height = checkboxStyle === 'circle' ? '22px' : '20px';
          span.style.borderRadius = checkboxStyle === 'circle' ? '50%' : '4px';
          span.style.border = `1px solid ${borderColor}`;
          span.style.backgroundColor = backgroundColor;

          // Update the node reference
          node = updatedNode;

          // using timeout to prevent color changed back to default color
          setTimeout(() => {
            span.style.color = isBackgroundLight ? '#000000' : '#ffffff';
          }, 0);

          return true; // Return true to indicate the update was successful
        },
        destroy: () => {
          // cleanup event listener
          wrapper?.removeEventListener('click', handleClick);
          if (this.options.hasCheckedAnimation) {
            wrapper?.removeEventListener('keydown', handleClick);
          }
        },
      };
    };
  },

  addCommands() {
    return {
      // Insert a checkbox into the editor
      insertCheckbox:
        (type) =>
        ({ commands }) => {
          const id = nanoid();
          return commands.insertContent({
            type: this.name,
            attrs: { id, isChecked: false, type },
          });
        },
      onChangeColor:
        (color: string, type: string) =>
        ({ commands }) => {
          const attrs: Record<string, any> = {};

          if (type === 'border') {
            attrs.borderColor = color;
          } else if (type === 'background') {
            attrs.backgroundColor = color;
          }

          return commands.updateAttributes(this.name, attrs);
        },
    };
  },
});

function findCellPositionAfterInsert(tr: Transaction, originalPos: number): number {
  // Create a mapping to keep track of the document changes
  const mapping = tr.mapping;

  // Use the mapping to find the updated position of the cell
  const newPos = mapping.map(originalPos);

  return newPos;
}

export function insertMultipleCheckbox(editor: Editor, type: CheckboxOptions['checkboxType']) {
  if (editor.view.state.selection instanceof CellSelection) {
    const { $anchorCell, $headCell } = editor.view.state.selection;
    let tr = editor.view.state.tr;

    const insertions: { pos: number; node: ProseMirrorModelNode; isReplacement: boolean }[] = [];

    editor.view.state.selection.forEachCell((node, pos) => {
      const cellStart = editor.view.state.doc.resolve(pos + 1);
      const cellEnd = editor.view.state.doc.resolve(pos + node.nodeSize - 1);
      const cellSelection = TextSelection.between(cellStart, cellEnd);

      // Always insert at the beginning of the cell content
      const insertPos = cellSelection.$from.pos;

      const id = nanoid();
      const checkboxNode = editor.schema.nodes['checkbox'].create({ id, isChecked: false, type });

      const isContainCheckbox = hasCheckboxNode(node);
      insertions.push({ pos: insertPos, node: checkboxNode, isReplacement: isContainCheckbox });
    });

    // Sort insertions in reverse order (highest position first)
    insertions.sort((a, b) => b.pos - a.pos);

    // Perform all insertions in a single transaction
    insertions.forEach(({ pos, node, isReplacement }) => {
      if (isReplacement) {
        // get end position of current node, we want to replace the entire node with the checkbox
        const endPos = tr.doc.resolve(pos).end();
        tr = tr.replaceWith(pos, endPos, node);
      } else {
        tr = tr.insert(pos, node);
      }
    });

    if (tr.docChanged) {
      editor.view.dispatch(tr);

      // Recalculate positions for the anchor and head cells in the updated document
      const updatedAnchorCell = findCellPositionAfterInsert(tr, $anchorCell.pos);
      const updatedHeadCell = findCellPositionAfterInsert(tr, $headCell.pos);

      if (updatedAnchorCell !== -1 && updatedHeadCell !== -1) {
        editor.commands.setCellSelection({
          anchorCell: updatedAnchorCell,
          headCell: updatedHeadCell,
        });
      }

      return true;
    }
  }
}

function hasCheckboxNode(node: ProseMirrorModelNode): boolean {
  // Base case: if this node is a checkbox, return true
  if (node.type.name === 'checkbox') {
    return true;
  }

  // If the node has content, recursively check its children
  if (node.content.size > 0) {
    let hasCheckbox = false;
    node.content.forEach((childNode) => {
      if (hasCheckboxNode(childNode)) {
        hasCheckbox = true;
        return false; // Stop iteration
      }
    });

    return hasCheckbox;
  }

  // If we've checked everything and found no checkbox, return false
  return false;
}
